/**
 * useCustomerCount — small data hook for surfaces that want to display
 * the LIVE customer count (e.g. the Leadership page stat strip).
 *
 * Calls `/api/integrations/ecosystem/snapshot` once per mount and reads
 * `cards.customerBase.exactCount`. Falls back to a safe "18,000+" string
 * whenever:
 *   • The API fails / times out
 *   • The backend gate `PUBLIC_SHOW_EXACT_CUSTOMER_COUNT` is `false`
 *     (in which case `exactCount` is `null` and `value` is the
 *     "Across Japan" phrase)
 *   • Upstream sister-sites are dark (no member_count signal)
 *
 * Returns:
 *   {
 *     value: string,        // "18,234" or "18,000+" (never empty)
 *     isLive: boolean,      // true ⇒ value came from a real API call
 *     loading: boolean,     // first-fetch in flight
 *     error: Error | null,
 *     sourceFreshness: [],  // same shape SourceFreshnessBadge expects —
 *                           // straight from the snapshot response, the
 *                           // backend's own recorded last-successful-
 *                           // sync time (never the browser's load time)
 *   }
 *
 * Owner directive (Feb 2026): "It needs to pull in the API data there,
 * so it shows an accurate number of people. And a fallback if the API
 * fails or the connection's not there, it should say '18,000+'."
 *
 * Owner directive (Feb 2026, updated): the Leadership page must surface
 * the JapanETCcard customer count ONLY — JapanTollReceipts customers must
 * NOT be summed in. Pass `partner: "japanetccard"` to scope the count.
 */

import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const FALLBACK = "18,000+";

export default function useCustomerCount({ partner } = {}) {
  const [state, setState] = useState({
    value: FALLBACK,
    isLive: false,
    loading: true,
    error: null,
    sourceFreshness: [],
  });

  useEffect(() => {
    let cancelled = false;
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    async function load() {
      try {
        const url = new URL(`${API}/api/integrations/ecosystem/snapshot`);
        if (partner) url.searchParams.set("partner", partner);
        const res = await fetch(url.toString(), {
          signal: controller?.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        const exact = body?.cards?.customerBase?.exactCount;
        const freshness = Array.isArray(body?.sourceFreshness) ? body.sourceFreshness : [];
        if (cancelled) return;
        if (typeof exact === "number" && exact > 0) {
          // Format with thousands separators (locale-agnostic).
          const formatted = exact.toLocaleString("en-US");
          setState({
            value: formatted,
            isLive: true,
            loading: false,
            error: null,
            sourceFreshness: freshness,
          });
        } else {
          // Backend gate is off OR upstream had no signal — keep fallback.
          setState({
            value: FALLBACK,
            isLive: false,
            loading: false,
            error: null,
            sourceFreshness: freshness,
          });
        }
      } catch (err) {
        if (cancelled || err?.name === "AbortError") return;
        setState({
          value: FALLBACK,
          isLive: false,
          loading: false,
          error: err,
          sourceFreshness: [],
        });
      }
    }

    load();
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [partner]);

  return state;
}
